import { Dispatch, SetStateAction, useContext, useMemo, useState } from "react";
import { UserContext } from "contexts/UserContext/UserContext";
import { CircularProgress, IconButton, List } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ClearIcon from "@mui/icons-material/Clear";
import CheckIcon from "@mui/icons-material/Check";
import { IEventReportDetails } from "entities/EventReports/EventReportsEntityInterfaces";
import { DetailsReportEditionContext } from "contexts/DetailsReportEditionContext/DetailsReportEditionContext";
import { EventReportsEntity } from "entities/EventReports/EventReportsEntity";
import { ApolloQueryResult, OperationVariables } from "@apollo/client";
import { FAILURE_TYPES_CODE_EXCEPTIONS } from "constants/constants";

export const EditionOptionsCell = ({
    row,
    editingRow,
    refetch,
    setEditioMode,
}: {
    row: IEventReportDetails;
    editingRow: boolean;
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>;
    setEditioMode: Dispatch<
        SetStateAction<
            {
                id: number;
            }[]
        >
    >;
}) => {
    const { user_id } = useContext(UserContext);
    const { edit, setEdit } = useContext(DetailsReportEditionContext);
    const [saving, setSaving] = useState<boolean>(false);

    const { updateEventReportMutation } = EventReportsEntity.UpdateEventReportBO();

    const existingEdition = useMemo(() => edit.findIndex((event) => event.id === row.event_report_id), [edit, row]);
    const selectedFailureTypeIdIsNotSelected = useMemo(
        () =>
            row.failure_type.failure_type_id === edit[existingEdition]?.changes?.failure_type_id &&
            row.failure_type.code === FAILURE_TYPES_CODE_EXCEPTIONS.NOT_SELECTED,
        [row, existingEdition, edit],
    );
    const selectedAreaId = useMemo(() => edit[existingEdition]?.changes?.area_id, [existingEdition, edit]);
    const requiredFailureFields =
        row.failure_type.code === FAILURE_TYPES_CODE_EXCEPTIONS.BELOW_TRIGGER ||
        selectedFailureTypeIdIsNotSelected ||
        (edit[existingEdition]?.changes?.failure_type_id && edit[existingEdition]?.changes?.failure_code_id);
    const requiredFieldsAdded = useMemo(
        () =>
            selectedAreaId
                ? Boolean(edit[existingEdition]?.changes?.functional_location_id && requiredFailureFields)
                : Boolean(edit[existingEdition]?.changes?.failure_type_id && requiredFailureFields),
        [edit, existingEdition, selectedAreaId, requiredFailureFields],
    );

    return (
        <List sx={{ display: "flex", alignItems: "center" }}>
            {saving ? (
                <CircularProgress size={20} color="info" />
            ) : editingRow ? (
                <IconButton
                    disabled={!requiredFieldsAdded || !edit[existingEdition]?.changes}
                    size="small"
                    onClick={async () => {
                        setSaving(true);
                        if (existingEdition !== -1) {
                            if (edit[existingEdition]?.changes) {
                                const changes = {
                                    ...edit[existingEdition]?.changes,
                                    ...(!edit[existingEdition].location_specific && {
                                        area_id: null,
                                        functional_location_id: null,
                                    }),
                                };
                                await updateEventReportMutation({
                                    variables: {
                                        eventReportId: row.event_report_id,
                                        changes: changes,
                                    },
                                });
                                await refetch();
                            }
                        }
                        setEdit((prevEdit) => prevEdit.filter((event) => event.id !== edit[existingEdition].id));
                        setEditioMode((prevEditionMode) =>
                            prevEditionMode.filter((event) => event.id !== edit[existingEdition].id),
                        );
                        setSaving(false);
                    }}
                >
                    <CheckIcon />
                </IconButton>
            ) : (
                <IconButton
                    size="small"
                    onClick={() => {
                        if (existingEdition !== -1) {
                            setEdit((prevEdit) => {
                                const newEdit = [...prevEdit];
                                newEdit[existingEdition] = {
                                    ...newEdit[existingEdition],
                                    changes: {
                                        ...(row.area?.area_id && {
                                            area_id: row.area?.area_id,
                                        }),
                                        ...(row.functional_location?.functional_location_id && {
                                            functional_location_id: row.functional_location?.functional_location_id,
                                        }),
                                        failure_type_id: row.failure_type.failure_type_id,
                                        failure_code_id: row.failure_code?.failure_code_id,
                                        user_id: row.user_operator.user_id,
                                        ...(row.notes && { notes: row.notes }),
                                        ...(user_id && { edited_by_user_id: user_id }),
                                    },
                                };
                                return newEdit;
                            });
                        } else {
                            setEdit((prevEdit) => {
                                return [
                                    ...prevEdit,
                                    {
                                        id: row.event_report_id,
                                        location_specific: row.failure_type.is_location_specific,
                                        changes: {
                                            ...(row.area?.area_id && {
                                                area_id: row.area?.area_id,
                                            }),
                                            ...(row.functional_location?.functional_location_id && {
                                                functional_location_id: row.functional_location?.functional_location_id,
                                            }),
                                            failure_type_id: row.failure_type.failure_type_id,
                                            failure_code_id: row.failure_code?.failure_code_id,
                                            user_id: row.user_operator.user_id,
                                            ...(row.notes && { notes: row.notes }),
                                            ...(user_id && { edited_by_user_id: user_id }),
                                        },
                                    },
                                ];
                            });
                        }
                        setEditioMode((prevEditionMode) => [...prevEditionMode, { id: row.event_report_id }]);
                    }}
                >
                    <EditIcon />
                </IconButton>
            )}
            <IconButton
                size="small"
                disabled={!editingRow}
                onClick={() => {
                    setEdit((prevEdit) => prevEdit.filter((event) => event.id !== edit[existingEdition].id));
                    setEditioMode((prevEditionMode) =>
                        prevEditionMode.filter((event) => event.id !== edit[existingEdition].id),
                    );
                }}
            >
                <ClearIcon />
            </IconButton>
        </List>
    );
};
